import { Badge } from 'src/design/atoms/Badge';
import { List } from 'src/design/atoms/List';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Drawer } from 'src/design/molecules/Drawer';
import React from 'react';
import { AllScopes } from 'src/app/authorization/AllScopes';
import { useDialogs } from 'src/hooks/useDialogs';

/** @registry Drawer explaining the scope format and access-level model, with the full scope list. */
export const ScopeInfoDialog: React.FC = () => {
  const { scopeInfoOpen, setScopeInfoOpen } = useDialogs();

  const closeDrawer = () => {
    setScopeInfoOpen(false);
  };

  return (
    <Drawer
      opened={scopeInfoOpen}
      onClose={closeDrawer}
      title="Understanding Scopes"
      position="right"
    >
      <Stack gap="md">
        <div>
          <Title order={4}>Overview</Title>
          <Text>
            Scopes control what resources a user with a role can read, write,
            and/or manage. Each type of data (referred to as resources) within
            the system (ie Documents, tasks, materials, timesheets, etc) has a
            series of corresponding scopes used to control user access.
          </Text>
        </div>

        <div>
          <Stack gap="md">
            <Title order={4}>Scope Format</Title>
            <Text>
              A scope is composed of three parts, the{' '}
              <Text component="span" fw={700}>
                resource
              </Text>
              , the{' '}
              <Text component="span" fw={700}>
                permission
              </Text>
              , and the{' '}
              <Text component="span" fw={700}>
                access level
              </Text>{' '}
              represented by a colon delimited list (
              <Badge variant="light" color="blue" fw={700}>
                Resource:permission:access level
              </Badge>
              ).
            </Text>
            <Text>
              <Text component="span" fw={700}>
                Resource:
              </Text>{' '}
              Type of data the permissions apply to. Documents, Dailys,
              Timesheets, Tasks, and Finance are all examples of resources.
            </Text>
            <Text>
              <Text component="span" fw={700}>
                Permission:
              </Text>{' '}
              What the user is allowed to do with specified resource.
            </Text>
            <List>
              <List.Item>
                <Text component="span" fw={700}>
                  Read
                </Text>
                : Allows the user to ONLY view the data.
              </List.Item>
              <List.Item>
                <Text component="span" fw={700}>
                  Write
                </Text>
                : Allows the user to view, update, or delete the specified
                resource regardless of who created it. Write permissions is
                synonymous with "admin" privileges for the resource.
              </List.Item>
              <List.Item>
                <Text component="span" fw={700}>
                  Restricted
                </Text>
                : Allows the user to view, update, or delete the specified
                resource that was originally created by the user.
              </List.Item>
            </List>
          </Stack>
        </div>

        <div>
          <Stack gap="md">
            <Text>
              <Text component="span" fw={700}>
                Access Level:
              </Text>{' '}
              Each resource can either be accessed system wide, or only within
              jobs the user is granted access to.
            </Text>
            <List>
              <List.Item>
                <Text component="span" fw={700}>
                  Global Access:
                </Text>{' '}
                The access level "all" is used to give access to all instances
                of the resource within the system regardless of whether the user
                has explicit access to the linked Job. For example,{' '}
                <Badge variant="light" color="blue" fw={700}>
                  Timesheets:read:global
                </Badge>{' '}
                allows the user to view all timesheets in the system, even if
                the user cannot see the associate Job.
              </List.Item>
              <List.Item>
                <Text component="span" fw={700}>
                  Job Specific Access:
                </Text>{' '}
                Alternatively, scopes can be used to allow access to ONLY the
                resources linked to Jobs the user has access to. For example,{' '}
                <Badge variant="light" color="blue" fw={700}>
                  Timesheets:write:jobs
                </Badge>{' '}
                allows the user to create, read, update, and delete timesheets
                ONLY if they are associate to Jobs that the user has been
                explicitly granted access to.
              </List.Item>
            </List>
          </Stack>
        </div>

        <div>
          <Stack gap="md">
            <Title order={4}>Scenarios</Title>
            <Text fw={600}>Scenario 1 - Materials Manager:</Text>
            <Text>
              Your organization has recently hired a Foreman whose job it is to
              manage all materials and pieces across all jobs on top of his/her
              normal Foreman responsibilities. By default, the foreman role
              gives access to most things within jobs the user has been granted
              access to. A system administrator would then create another role
              called "Materials Manager" and select the scopes{' '}
              <Badge variant="light" color="blue" fw={700}>
                Materials:write:global
              </Badge>{' '}
              and{' '}
              <Badge variant="light" color="blue" fw={700}>
                Pieces:write:global
              </Badge>
              . This will give the user access to create, view, update, or
              delete all materials and pieces regardless of the Job.
            </Text>
            <Text fw={600}>Scenario 2 - Contract Designer:</Text>
            <Text>
              Your organization has recently contracted a designer to provide
              designs for a single project. The designer is expected to upload
              and manage their design documents within jobs the user is assigned
              to. A system administrator would create a new role called
              "Designer" and assign the scope{' '}
              <Badge variant="light" color="blue" fw={700}>
                Documents:restricted:jobs
              </Badge>
              . If no other roles are assigned to the user, they will only have
              access to upload documents to Jobs they have been added to as well
              update and delete those documents.
            </Text>
          </Stack>
        </div>

        <div>
          <Title order={4}>Available Scopes</Title>
          <AllScopes hideLabel />
        </div>
      </Stack>
    </Drawer>
  );
};
